import { IonButton, IonButtons, IonCard, IonContent, IonFab, IonIcon, IonInput, IonItem, IonLabel, IonList, IonModal, IonSearchbar, IonSelect, IonSelectOption, IonText, IonTitle, IonToolbar, isPlatform } from '@ionic/react'
import { chevronForwardCircleOutline } from 'ionicons/icons'
import { useMemo, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Category } from '../../../@types/Category'
import { Template } from '../../../@types/template'
import { User } from '../../../@types/user'
import InputErrorMessage from '../../../components/InputErrorMessage'
import UserAvatar from '../../../components/UserAvatar'
import { setCreateStep, setDocumentCreateData } from '../../../store/massiveDocumentCreate'
import { findTemplateQuestions } from '../../../store/massiveDocumentCreate/actions/findTemplateQuestions'
import { selectFormData } from '../../../store/massiveDocumentCreate/selectors/selectFormData'
import { selectlegalRepList } from '../../../store/massiveDocumentCreate/selectors/selectLegalRepList'
import { selectTemplateList } from '../../../store/massiveDocumentCreate/selectors/selectTemplateList'
import { selectDocumentCategory } from '../../../store/utils/selectors/selectDocumentCategory'
import { useParams } from 'react-router-dom'
import { SignatureProvider } from '../../../@types/signatureProvider'
import { selectSignatureProviders } from '../../../store/signatureProviders/selectors/selectSignatureProviders'
import { createArchive } from '../../../store/documentCreate/actions/createArchiveBasic'
import { useRef } from 'react'
import { useEffect } from 'react'
import { findCategory } from '../../../store/utils/actions/findCategory'

export const DocumentCreateFirstStep = () => {
  const dispatch = useDispatch()
  const fileInput = useRef<HTMLInputElement>(null)
  const { userId }: { userId: string } = useParams()
  const documentCategoryList = useSelector(selectDocumentCategory)
  const legalRepList = useSelector(selectlegalRepList)
  const templateList = useSelector(selectTemplateList)
  const signatureProviders = useSelector(selectSignatureProviders)

  const [disableSubmit, setDisableSubmit] = useState<boolean>()
  const formData: any = useSelector(selectFormData)

  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    getValues // ✅ para sincronizar con el store cuando cambia "creationType"
  } = useForm({
    defaultValues: {
      ...formData,
      creationType: '',
      archive: null,
      archiveFilename: ''
    }
  })

  const [showLegalRepSelector, setShowLegalRepSelector] = useState(false)
  const [legalRepSearchtext, setLegalRepSearchtext] = useState('')

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setDisableSubmit(true)
      try {
        const response = await dispatch<any>(createArchive(file))
        if (response?.payload) {
          setValue('archive', response.payload)
          setValue('archiveFilename', file.name)
        }
      } finally {
        setDisableSubmit(false)
      }
    }
  }

  const onSubmit = (data: { title: string; category: Category; template?: Template; legalRep: User; signatureProvider: SignatureProvider; creationType: string; archive?: any; archiveFilename?: string }) => {
    setDisableSubmit(true)

    if (!data.legalRep) {
      // @ts-ignore
      delete data.legalRep;
    }

    // Validación principal: debe seleccionar una plantilla o subir un archivo
    if (data.creationType === 'plantilla' && !data.template) {
      alert('Debe seleccionar una plantilla antes de continuar.')
      setDisableSubmit(false)
      return
    }

    if (data.creationType === 'archivo' && !data.archive) {
      alert('Debe subir un archivo PDF antes de continuar.')
      setDisableSubmit(false)
      return
    }

    if (data.creationType === 'plantilla') {
      dispatch(findTemplateQuestions(data.template!.id.toString()))
    }

    dispatch(setDocumentCreateData(data))
    dispatch(setCreateStep(2))
  }

  useEffect(() => {
    dispatch(findCategory())
  }, [dispatch])

  const controllers = useMemo(() => {
    return [
      ({ field: { onChange, value } }: any) => (
        <IonInput
          value={value}
          placeholder="Ej: Contrato"
          onIonChange={({ detail: { value } }) => onChange(value)}
          {...register('title', {
            required: 'Campo obligatorio.'
          })}
        />
      ),
      ({ field: { onChange, value } }: any) => (
        <IonSelect
          value={value}
          okText="Aceptar"
          cancelText="Cancelar"
          placeholder="Seleccionar"
          onIonChange={({ detail: { value } }) => onChange(value)}
          {...register('category', {
            required: 'Campo obligatorio.'
          })}>
          {documentCategoryList.map((type) => (
            <IonSelectOption value={type} key={`option-select-${type.id}`}>
              {type.name}
            </IonSelectOption>
          ))}
        </IonSelect>
      )
    ]
  }, [])

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} className={isPlatform('desktop') ? 'ion-margin' : 'ion-no-margin ion-margin-vertical'}>
      <IonText color="primary">
        <h2>Paso 1 de 2</h2>
      </IonText>
      <IonCard style={{ maxWidth: 700, marginLeft: 0 }}>
        <section className="ion-padding">
          {/* TIPO DE CREACIÓN */}
          <IonItem lines="inset">
            <IonLabel position="stacked">Tipo de acuerdo masivo</IonLabel>
            <Controller
              name="creationType"
              control={control}
              rules={{
                required: 'Debe seleccionar el tipo de acuerdo masivo',
                validate: (value) => {
                  if (!value || value.trim() === '') return 'Debe seleccionar una opción válida';
                  return true;
                }
              }}
              render={({ field: { onChange, value } }: any) => (
                <IonSelect
                  value={value}
                  okText="Aceptar"
                  cancelText="Cancelar"
                  placeholder="Seleccionar"
                  onIonChange={({ detail: { value } }) => {
                    onChange(value)
                    if (value === 'archivo') {
                      setValue('template', null) // 🧹 limpiar plantilla
                    }
                    dispatch(setDocumentCreateData({ ...getValues(), creationType: value }))
                  }}>
                  <IonSelectOption value="plantilla">Plantilla</IonSelectOption>
                  <IonSelectOption value="archivo">Archivo</IonSelectOption>
                </IonSelect>
              )}
            />
          </IonItem>
          {errors.creationType && (
            <InputErrorMessage
              errorMessage={errors.creationType.message || ''}
              paddingLeft={16}
            />
          )}
          <br />

          {/* ARCHIVO PDF (solo si es "archivo") */}
          {watch('creationType') === 'archivo' && (
            <>
              <IonItem lines="inset" onClick={() => fileInput?.current?.click()} disabled={disableSubmit} button>
                <IonLabel position="stacked">Archivo PDF</IonLabel>
                <Controller
                  name="archiveFilename"
                  control={control}
                  render={({ field: { value } }: any) => (
                    <>
                      <input ref={fileInput} type="file" accept="application/pdf" onChange={onFileChange} hidden />
                      <IonInput
                        value={value}
                        placeholder="Seleccionar archivo"
                        readonly
                        {...register('archiveFilename', {
                          validate: () => {
                            const tipo = watch('creationType')
                            const archivoNombre = watch('archiveFilename')
                            if (tipo === 'archivo' && !archivoNombre) {
                              return 'Debe seleccionar un archivo PDF.'
                            }
                            return true
                          }
                        })}
                      />
                    </>
                  )}
                />
              </IonItem>
              {errors.archiveFilename && <InputErrorMessage errorMessage={errors.archiveFilename.message || ''} paddingLeft={16} />}
              <br />
            </>
          )}

          {/* TITULO */}
          <IonItem lines="inset">
            <IonLabel position="stacked">Titulo</IonLabel>
            <Controller name="title" control={control} render={controllers[0]} />
          </IonItem>
          {errors.title && <InputErrorMessage errorMessage={errors.title.message!} paddingLeft={16} />}
          <br />

          {/* TIPO DE DOCUMENTO */}
          <IonItem lines="inset">
            <IonLabel position="stacked">Tipo de documento</IonLabel>
            <Controller
              name="category"
              control={control}
              rules={{
                required: 'Debe seleccionar un tipo de documento'
              }}
              render={({ field: { onChange, value } }) => (
                <IonSelect
                  value={value}
                  okText="Aceptar"
                  cancelText="Cancelar"
                  placeholder="Seleccionar"
                  onIonChange={({ detail: { value } }) => onChange(value)}
                >
                  {documentCategoryList.map((type) => (
                    <IonSelectOption value={type} key={`option-select-${type.id}`}>
                      {type.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              )}
            />
          </IonItem>
          {errors.category && (
            <InputErrorMessage
              errorMessage={errors.category.message || ''}
              paddingLeft={16}
            />
          )}
          <br />

          {/* PLANTILLA (solo si es "plantilla") */}
          {watch('creationType') === 'plantilla' && (
            <>
              <IonItem lines="inset">
                <IonLabel position="stacked">Plantilla</IonLabel>
                <Controller
                  name="template"
                  control={control}
                  render={({ field: { onChange, value } }: any) => (
                    <IonSelect
                      value={value}
                      okText="Aceptar"
                      cancelText="Cancelar"
                      placeholder="Seleccionar"
                      onIonChange={({ detail: { value } }) => onChange(value)}
                      {...register('template', {
                        required: 'Campo obligatorio.'
                      })}>
                      {templateList.map((template) => (
                        <IonSelectOption value={template} key={`option-select-${template.id}`}>
                          {template.name}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  )}
                />
              </IonItem>
              {errors.template && <InputErrorMessage errorMessage={errors.template.message || ''} paddingLeft={16} />}
              <br />
            </>
          )}

          {/* PROVEEDOR DE FIRMA */}
          <IonItem lines="inset">
            <IonLabel position="stacked">Proveedor de firma</IonLabel>
            <Controller
              name="signatureProvider"
              control={control}
              render={({ field: { onChange, value } }: any) => (
                <IonSelect
                  value={value}
                  okText="Aceptar"
                  cancelText="Cancelar"
                  placeholder="Seleccionar"
                  onIonChange={({ detail: { value } }) => onChange(value)}
                  {...register('signatureProvider', {
                    required: 'Campo obligatorio.'
                  })}>
                  {signatureProviders.map((provider) => (
                    <IonSelectOption value={provider} key={`option-select-${provider.id}`}>
                      {provider.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              )}
            />
          </IonItem>
          {errors.signatureProvider && <InputErrorMessage errorMessage={errors.signatureProvider.message || ''} paddingLeft={16} />}
          <br />

          {/* REPRESENTANTE LEGAL */}
          <IonItem lines="inset" onClick={() => setShowLegalRepSelector(true)} button>
            <IonLabel position="stacked">Representante legal{userId != undefined ? ' (opcional)' : null}</IonLabel>
            <Controller
              name="legalRep"
              control={control}
              render={({ field: { value } }: any) => (
                <IonInput
                  value={value?.name}
                  placeholder="Seleccionar"
                  readonly
                />
              )}
            />
          </IonItem>

          {/* MODAL REPRESENTANTE LEGAL */}
          <IonModal isOpen={showLegalRepSelector}>
            <IonToolbar>
              <IonTitle>Representante legal</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowLegalRepSelector(false)}>Volver</IonButton>
              </IonButtons>
            </IonToolbar>
            <IonContent>
              <IonSearchbar value={legalRepSearchtext} onIonChange={({ detail: { value = '' } }) => setLegalRepSearchtext(value)} debounce={900} placeholder="Buscar" />
              <IonList>
                {legalRepList
                  .filter(({ user }: any) => user.name?.toLowerCase().includes(legalRepSearchtext.toLowerCase()))
                  .map((user: any) => (
                    <IonItem
                      onClick={() => {
                        setValue('legalRep', user.user)
                        setShowLegalRepSelector(false)
                      }}
                      button
                      detail
                      key={user.user.id}>
                      <UserAvatar user={user}></UserAvatar>
                      <IonLabel>
                        <h3 style={{ fontWeight: 500 }}>{user.user.name}</h3>
                      </IonLabel>
                    </IonItem>
                  ))}
              </IonList>
            </IonContent>
          </IonModal>
        </section>
      </IonCard>

      {/* Submit button */}
      <IonFab vertical="bottom" horizontal="end" slot="fixed">
        <IonButton fill="solid" size="large" color="secondary" type="submit" disabled={disableSubmit}>
          <span>Siguiente</span>
          <IonIcon icon={chevronForwardCircleOutline} slot="end"></IonIcon>
        </IonButton>
      </IonFab>
    </form>
  )
}
